import {
  $createListNode,
  $isListItemNode,
  $isListNode,
  ListExtension,
  type ListItemNode,
  type ListNode,
} from "@lexical/list";
import {
  $addUpdateTag,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COLLABORATION_TAG,
  defineExtension,
  HISTORIC_TAG,
  HISTORY_PUSH_TAG,
  type LexicalExtension,
} from "lexical";

const TASK_MARKER = /^\[( |x)?\] $/i;

/** Gives the item a check list of its own, keeping the items around it in lists of their old type. */
function $moveToCheckList(item: ListItemNode, list: ListNode): void {
  if (list.getChildrenSize() === 1) {
    list.setListType("check");
    return;
  }
  const following = item.getNextSiblings();
  const checkList = $createListNode("check");
  list.insertAfter(checkList);
  checkList.append(item);
  if (following.length > 0) {
    const rest = $createListNode(list.getListType(), list.getStart());
    checkList.insertAfter(rest);
    rest.append(...following);
  }
  if (list.getChildrenSize() === 0) list.remove();
}

/**
 * `- [ ] ` and `- [x] `, typed in sequence. By the time `[ ] ` is typed the `- ` has already made a
 * bulleted list, and @lexical/markdown only runs element shortcuts on top-level blocks, so its
 * CHECK_LIST transformer never sees text inside a list item. This covers exactly that gap, with the
 * same guards upstream uses: only while typing, never during composition, undo, or remote updates.
 */
export const TaskItemShortcutExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/task-item-shortcut",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/task-item-shortcut",
  dependencies: [ListExtension],
  register: (editor) =>
    editor.registerUpdateListener(({ tags, dirtyLeaves, editorState, prevEditorState }) => {
      if (tags.has(HISTORIC_TAG) || tags.has(COLLABORATION_TAG) || editor.isComposing()) return;
      const selection = editorState.read($getSelection);
      const previous = prevEditorState.read($getSelection);
      if (
        !$isRangeSelection(selection) ||
        !$isRangeSelection(previous) ||
        !selection.isCollapsed() ||
        selection.is(previous) ||
        !dirtyLeaves.has(selection.anchor.key)
      ) {
        return;
      }
      const { key, offset } = selection.anchor;
      const matches = editorState.read(() => {
        const node = $getNodeByKey(key);
        return $isTextNode(node) && TASK_MARKER.test(node.getTextContent().slice(0, offset));
      });
      if (!matches) return;

      editor.update(() => {
        const current = $getSelection();
        if (!$isRangeSelection(current) || !current.isCollapsed()) return;
        const node = current.anchor.getNode();
        if (!$isTextNode(node) || node.getFormat() !== 0) return;
        const item = node.getParent();
        const list = item?.getParent();
        if (!$isListItemNode(item) || !$isListNode(list) || item.getFirstChild() !== node) return;
        if (list.getListType() !== "bullet") return;
        const marker = TASK_MARKER.exec(node.getTextContent().slice(0, current.anchor.offset));
        if (marker === null) return;

        $moveToCheckList(item, list);
        item.setChecked(marker[1]?.toLowerCase() === "x");
        node.spliceText(0, marker[0].length, "", true);
        if (node.getTextContentSize() === 0) {
          node.remove();
          item.selectStart();
        }
        // Its own undo step, so undo brings the literal marker back.
        $addUpdateTag(HISTORY_PUSH_TAG);
      });
    }),
});
